import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  Checkbox,
  ListItemText,
  OutlinedInput
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  isActive: boolean;
  createdAt: string;
  remainingTasks?: number;
}

const Users: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Edit dialog
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [editFormData, setEditFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    role: '',
    assignedAdminIds: [] as string[]
  });
  
  // Password dialog
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // Create user dialog
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createFormData, setCreateFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'ADMIN',
    assignedAdminIds: [] as string[]
  });
  
  // Authenticator dialog
  const [showAuthenticatorDialog, setShowAuthenticatorDialog] = useState(false);
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [verifying2FA, setVerifying2FA] = useState(false);
  const [twoFAVerified, setTwoFAVerified] = useState(false);
  
  const [saving, setSaving] = useState(false);
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setUsers(response.data.data.users || []);
      }
    } catch (err: any) {
      console.error('Error fetching users:', err);
      setError(err.response?.data?.message || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (user: User) => {
    setSelectedUser(user);
    // Extract assigned admin IDs from the assignedAdmins relation
    const adminIds = ((user as any).assignedAdmins || []).map((a: any) => a.admin?.id || a.adminId).filter(Boolean);
    // Fallback to legacy single assignedAdminId if no multi-admin records
    const fallbackIds = adminIds.length > 0 ? adminIds : ((user as any).assignedAdminId ? [(user as any).assignedAdminId] : []);
    setEditFormData({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      role: user.role,
      assignedAdminIds: fallbackIds
    });
    setShowEditDialog(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedUser) return;

    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.put(
        `${API_BASE_URL}/users/${selectedUser.id}`,
        editFormData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setSuccess('User updated successfully');
        setShowEditDialog(false);
        fetchUsers();
      }
    } catch (err: any) {
      console.error('Error updating user:', err);
      setError(err.response?.data?.message || 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = (user: User) => {
    setSelectedUser(user);
    setNewPassword('');
    setConfirmPassword('');
    setShowPasswordDialog(true);
  };

  const handleSavePassword = async () => {
    if (!selectedUser) return;

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const response = await axios.patch(
        `${API_BASE_URL}/users/${selectedUser.id}/password`,
        { newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setSuccess('Password changed successfully');
        setShowPasswordDialog(false);
        setNewPassword('');
        setConfirmPassword('');
      }
    } catch (err: any) {
      console.error('Error changing password:', err);
      setError(err.response?.data?.message || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateUser = async () => {
    if (!createFormData.firstName || !createFormData.lastName || !createFormData.email || !createFormData.password) {
      setError('All fields are required');
      return;
    }

    if (createFormData.password !== createFormData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (createFormData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    // Validate assignedAdminIds for CONTRIBUTOR role
    if (createFormData.role === 'CONTRIBUTOR' && createFormData.assignedAdminIds.length === 0) {
      setError('Please assign at least one admin to this contributor');
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/users`,
        {
          firstName: createFormData.firstName,
          lastName: createFormData.lastName,
          email: createFormData.email,
          password: createFormData.password,
          role: createFormData.role,
          assignedAdminIds: createFormData.role === 'CONTRIBUTOR' ? createFormData.assignedAdminIds : undefined
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setSuccess('User created successfully');
        setShowCreateDialog(false);
        setCreateFormData({
          firstName: '',
          lastName: '',
          email: '',
          password: '',
          confirmPassword: '',
          role: 'ADMIN',
          assignedAdminIds: []
        });
        fetchUsers();
      }
    } catch (err: any) {
      console.error('Error creating user:', err);
      setError(err.response?.data?.message || 'Failed to create user');
    } finally {
      setSaving(false);
    }
  };

  const handleResetAuthenticator = async (user: User) => {
    if (!window.confirm(`Reset authenticator for ${user.firstName} ${user.lastName}?`)) {
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/users/${user.id}/reset-2fa`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setSuccess('Authenticator reset successfully. User will need to set up 2FA again.');
        fetchUsers();
      }
    } catch (err: any) {
      console.error('Error resetting authenticator:', err);
      setError(err.response?.data?.message || 'Failed to reset authenticator');
    } finally {
      setSaving(false);
    }
  };

  const handleSetupAuthenticator = async (user: User) => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/users/${user.id}/setup-2fa`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setQrCode(response.data.data.qrCode);
        setSecret(response.data.data.secret);
        setSelectedUser(user);
        setVerificationCode('');
        setTwoFAVerified(false);
        setShowAuthenticatorDialog(true);
      }
    } catch (err: any) {
      console.error('Error setting up authenticator:', err);
      setError(err.response?.data?.message || 'Failed to setup authenticator');
    } finally {
      setSaving(false);
    }
  };

  const handleVerify2FA = async () => {
    if (!selectedUser || !verificationCode || verificationCode.length !== 6) {
      setError('Please enter a valid 6-digit verification code');
      return;
    }
    try {
      setVerifying2FA(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/users/${selectedUser.id}/verify-2fa`,
        { code: verificationCode },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setTwoFAVerified(true);
        setSuccess('2FA activated successfully! User will need to enter code on next login.');
        fetchUsers();
      }
    } catch (err: any) {
      console.error('Error verifying 2FA:', err);
      setError(err.response?.data?.message || 'Invalid verification code. Please try again.');
    } finally {
      setVerifying2FA(false);
    }
  };

  const handleToggleStatus = async (user: User) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.patch(
        `${API_BASE_URL}/users/${user.id}/status`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setSuccess(`User ${user.isActive ? 'deactivated' : 'activated'} successfully`);
        fetchUsers();
      }
    } catch (err: any) {
      console.error('Error toggling user status:', err);
      setError(err.response?.data?.message || 'Failed to toggle user status');
    }
  };

  // Paginated data
  const paginatedUsers = users.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // Handle page change
  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom fontWeight="bold">
        User Management
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Manage system users, roles, and permissions (Super Admin Only)
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Add New User Button */}
      {currentUser?.role === 'SUPER_ADMIN' && (
        <Box sx={{ mb: 3 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setShowCreateDialog(true)}
          >
            Add New User
          </Button>
        </Box>
      )}

      <Card>
        <CardContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Name</strong></TableCell>
                    <TableCell><strong>Email</strong></TableCell>
                    <TableCell><strong>Role</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell><strong>Assigned Admins</strong></TableCell>
                    {currentUser?.role === 'SUPER_ADMIN' && (
                      <TableCell><strong>Remaining Tasks</strong></TableCell>
                    )}
                    <TableCell><strong>Created At</strong></TableCell>
                    <TableCell align="center"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedUsers.map((user) => (
                    <TableRow key={user.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {user.firstName} {user.lastName}
                        </Typography>
                      </TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <Chip
                          label={
                            user.role === 'SUPER_ADMIN' ? 'Super Admin' : 
                            user.role === 'ADMIN' ? 'Admin' : 
                            user.role === 'CONTRIBUTOR' ? 'Contributor' : 'User'
                          }
                          color={
                            user.role === 'SUPER_ADMIN' ? 'secondary' : 
                            user.role === 'ADMIN' ? 'primary' : 
                            user.role === 'CONTRIBUTOR' ? 'info' : 'default'
                          }
                          size="small"
                          sx={{ color: user.role === 'SUPER_ADMIN' || user.role === 'ADMIN' || user.role === 'CONTRIBUTOR' ? '#fff' : '#333', fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={user.isActive ? 'Active' : 'Inactive'}
                          color={user.isActive ? 'success' : 'default'}
                          size="small"
                          sx={{ color: user.isActive ? '#fff' : '#333', fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell>
                        {user.role === 'CONTRIBUTOR' && (user as any).assignedAdmins?.length > 0 ? (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {(user as any).assignedAdmins.map((a: any) => (
                              <Chip
                                key={a.admin?.id || a.adminId}
                                label={a.admin ? `${a.admin.firstName} ${a.admin.lastName}` : 'Unknown'}
                                size="small"
                                variant="outlined"
                                color="primary"
                              />
                            ))}
                          </Box>
                        ) : user.role === 'CONTRIBUTOR' ? (
                          <Typography variant="caption" color="text.secondary">None assigned</Typography>
                        ) : (
                          <Typography variant="caption" color="text.secondary">-</Typography>
                        )}
                      </TableCell>
                      {currentUser?.role === 'SUPER_ADMIN' && (
                        <TableCell>
                          <Chip
                            label={`${user.remainingTasks || 0} tasks`}
                            color={user.remainingTasks && user.remainingTasks > 0 ? 'warning' : 'default'}
                            size="small"
                            sx={{ color: user.remainingTasks && user.remainingTasks > 0 ? '#fff' : '#333', fontWeight: 600 }}
                          />
                        </TableCell>
                      )}
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(user.createdAt).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Edit User">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleEdit(user)}
                            sx={{ mr: 0.5 }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Change Password">
                          <IconButton
                            size="small"
                            color="warning"
                            onClick={() => handleChangePassword(user)}
                            sx={{ mr: 0.5 }}
                          >
                            <Typography sx={{ fontSize: '16px' }}>🔑</Typography>
                          </IconButton>
                        </Tooltip>
                        {user.role === 'SUPER_ADMIN' && (
                          <>
                            <Tooltip title="Setup 2FA">
                              <IconButton
                                size="small"
                                color="info"
                                onClick={() => handleSetupAuthenticator(user)}
                                sx={{ mr: 0.5 }}
                              >
                                <Typography sx={{ fontSize: '16px' }}>📱</Typography>
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Reset 2FA">
                              <IconButton
                                size="small"
                                color="secondary"
                                onClick={() => handleResetAuthenticator(user)}
                                sx={{ mr: 0.5 }}
                              >
                                <Typography sx={{ fontSize: '16px' }}>🔄</Typography>
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                        <Tooltip title={user.isActive ? 'Deactivate User' : 'Activate User'}>
                          <IconButton
                            size="small"
                            color={user.isActive ? 'error' : 'success'}
                            onClick={() => handleToggleStatus(user)}
                          >
                            {user.isActive ? <DeleteIcon fontSize="small" /> : <Typography sx={{ fontSize: '16px' }}>✅</Typography>}
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          
          {/* Pagination - show when data exists */}
          {users.length > 0 && (
            <TablePagination
              component="div"
              count={users.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[25, 50, 100, 250, 500]}
              showFirstButton
              showLastButton
            />
          )}
        </CardContent>
      </Card>

      {/* Edit User Dialog */}
      <Dialog open={showEditDialog} onClose={() => setShowEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="First Name"
              value={editFormData.firstName}
              onChange={(e) => setEditFormData({ ...editFormData, firstName: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Last Name"
              value={editFormData.lastName}
              onChange={(e) => setEditFormData({ ...editFormData, lastName: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={editFormData.email}
              onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              select
              label="Role"
              value={editFormData.role}
              onChange={(e) => setEditFormData({ ...editFormData, role: e.target.value })}
              sx={{ mb: 2 }}
              SelectProps={{ native: true }}
            >
              <option value="ADMIN">Admin</option>
              <option value="SUPER_ADMIN">Super Admin</option>
              <option value="USER">User</option>
              <option value="CONTRIBUTOR">Contributor</option>
            </TextField>
            {editFormData.role === 'CONTRIBUTOR' && (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Assigned Admins *</InputLabel>
                <Select
                  multiple
                  value={editFormData.assignedAdminIds}
                  onChange={(e) => setEditFormData({ ...editFormData, assignedAdminIds: typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value as string[] })}
                  input={<OutlinedInput label="Assigned Admins *" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(selected as string[]).map((id) => {
                        const admin = users.find(u => u.id === id);
                        return admin ? (
                          <Chip key={id} label={`${admin.firstName} ${admin.lastName}`} size="small" />
                        ) : null;
                      })}
                    </Box>
                  )}
                >
                  {users
                    .filter(u => u.role === 'ADMIN' || u.role === 'SUPER_ADMIN')
                    .map(admin => (
                      <MenuItem key={admin.id} value={admin.id}>
                        <Checkbox checked={editFormData.assignedAdminIds.indexOf(admin.id) > -1} />
                        <ListItemText primary={`${admin.firstName} ${admin.lastName} (${admin.role})`} />
                      </MenuItem>
                    ))}
                </Select>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>
                  Select one or more admins to handle this contributor's uploads. Data will be distributed equally.
                </Typography>
              </FormControl>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowEditDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained" disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={showPasswordDialog} onClose={() => setShowPasswordDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Changing password for: <strong>{selectedUser?.firstName} {selectedUser?.lastName}</strong>
            </Typography>
            <TextField
              fullWidth
              label="New Password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              sx={{ mb: 2 }}
              helperText="Minimum 6 characters"
            />
            <TextField
              fullWidth
              label="Confirm Password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPasswordDialog(false)}>Cancel</Button>
          <Button onClick={handleSavePassword} variant="contained" color="warning" disabled={saving}>
            {saving ? 'Changing...' : 'Change Password'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={showCreateDialog} onClose={() => setShowCreateDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New User</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="First Name"
              value={createFormData.firstName}
              onChange={(e) => setCreateFormData({ ...createFormData, firstName: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Last Name"
              value={createFormData.lastName}
              onChange={(e) => setCreateFormData({ ...createFormData, lastName: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={createFormData.email}
              onChange={(e) => setCreateFormData({ ...createFormData, email: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              select
              label="Role"
              value={createFormData.role}
              onChange={(e) => setCreateFormData({ ...createFormData, role: e.target.value })}
              sx={{ mb: 2 }}
              SelectProps={{ native: true }}
            >
              <option value="ADMIN">Admin</option>
              <option value="SUPER_ADMIN">Super Admin</option>
              <option value="USER">User</option>
              <option value="CONTRIBUTOR">Contributor</option>
            </TextField>
            {createFormData.role === 'CONTRIBUTOR' && (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Assigned Admins *</InputLabel>
                <Select
                  multiple
                  value={createFormData.assignedAdminIds}
                  onChange={(e) => setCreateFormData({ ...createFormData, assignedAdminIds: typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value as string[] })}
                  input={<OutlinedInput label="Assigned Admins *" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(selected as string[]).map((id) => {
                        const admin = users.find(u => u.id === id);
                        return admin ? (
                          <Chip key={id} label={`${admin.firstName} ${admin.lastName}`} size="small" />
                        ) : null;
                      })}
                    </Box>
                  )}
                >
                  {users
                    .filter(u => u.role === 'ADMIN' || u.role === 'SUPER_ADMIN')
                    .map(admin => (
                      <MenuItem key={admin.id} value={admin.id}>
                        <Checkbox checked={createFormData.assignedAdminIds.indexOf(admin.id) > -1} />
                        <ListItemText primary={`${admin.firstName} ${admin.lastName} (${admin.role})`} />
                      </MenuItem>
                    ))}
                </Select>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>
                  Select one or more admins. Uploaded data will be distributed equally among them.
                </Typography>
              </FormControl>
            )}
            <TextField
              fullWidth
              label="Password"
              type="password"
              value={createFormData.password}
              onChange={(e) => setCreateFormData({ ...createFormData, password: e.target.value })}
              sx={{ mb: 2 }}
              helperText="Minimum 6 characters"
            />
            <TextField
              fullWidth
              label="Confirm Password"
              type="password"
              value={createFormData.confirmPassword}
              onChange={(e) => setCreateFormData({ ...createFormData, confirmPassword: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCreateDialog(false)}>Cancel</Button>
          <Button onClick={handleCreateUser} variant="contained" color="primary" disabled={saving}>
            {saving ? 'Creating...' : 'Create User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Authenticator Setup Dialog */}
      <Dialog open={showAuthenticatorDialog} onClose={() => setShowAuthenticatorDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Setup Two-Factor Authentication</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, textAlign: 'center' }}>
            {twoFAVerified ? (
              <Alert severity="success" sx={{ mb: 2 }}>
                2FA has been activated successfully for {selectedUser?.firstName} {selectedUser?.lastName}!
              </Alert>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Step 1: Scan this QR code with Google Authenticator or any TOTP app
                </Typography>
                {qrCode && (
                  <Box sx={{ mb: 2 }}>
                    <img src={qrCode} alt="QR Code" style={{ maxWidth: '250px' }} />
                  </Box>
                )}
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Or enter this secret manually:
                </Typography>
                <TextField
                  fullWidth
                  value={secret}
                  InputProps={{ readOnly: true }}
                  size="small"
                  sx={{ mb: 3 }}
                />
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Step 2: Enter the 6-digit code from the authenticator app to verify and activate
                </Typography>
                <TextField
                  fullWidth
                  label="Verification Code"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="Enter 6-digit code"
                  inputProps={{ maxLength: 6, style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5rem' } }}
                  sx={{ mb: 2 }}
                />
                <Alert severity="warning" sx={{ textAlign: 'left' }}>
                  2FA will NOT be activated until the code is verified. The user must scan the QR code and enter the code to complete setup.
                </Alert>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          {twoFAVerified ? (
            <Button onClick={() => setShowAuthenticatorDialog(false)} variant="contained">
              Done
            </Button>
          ) : (
            <>
              <Button onClick={() => setShowAuthenticatorDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleVerify2FA}
                variant="contained"
                color="primary"
                disabled={verifying2FA || verificationCode.length !== 6}
              >
                {verifying2FA ? 'Verifying...' : 'Verify & Activate'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Users;
